import { Component, DestroyRef, OnInit, computed, inject, input, output, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { TabsModule } from 'primeng/tabs';
import { TagModule } from 'primeng/tag';
import {
  FormAiOutcome,
  FormEvents,
  FormPublicAccess,
  FormScript,
  FormSubmitAction,
  FormWebhook
} from '../../../shared/dynamic-form/form-schema.models';
import { FormBuilderActionsPanelComponent } from './form-builder-actions-panel.component';
import { FormBuilderAiOutcomePanelComponent } from './form-builder-ai-outcome-panel.component';
import { FormBuilderEditCultureService } from './form-builder-edit-culture.service';
import { FormBuilderLocalizedInputComponent } from './form-builder-localized-input.component';
import { FormBuilderPreviewComponent } from './form-builder-preview.component';
import { FormBuilderThemePanelComponent } from './form-builder-theme-panel.component';
import { FormBuilderLogicPanelComponent } from './form-builder-logic-panel.component';
import { FormBuilderPublishPanelComponent } from './form-builder-publish-panel.component';
import { finalize, forkJoin, of, switchMap } from 'rxjs';
import { CurrentContextService } from '../../../core/context/current-context.service';
import { LocalizationService } from '../../../core/localization/localization.service';
import { InfoHintComponent } from '../../../shared/design-system/info-hint.component';
import { AinStateComponent } from '../../../shared/design-system/state.component';
import {
  FORM_LAYOUT_TYPES,
  FormBuilderDraftPayload,
  FormField,
  FormFieldType,
  FormLayoutType,
  FormSchemaModel,
  applyActivePublicationSlug,
  collectFieldKeys,
  createDefaultAiOutcome,
  createEmptySchema,
  findField,
  migrateLayoutSchema,
  parseBuilderDraft,
  parseFormSchema,
  readLocalizedText,
  serializeBuilderDraft,
  serializeFormSchema
} from '../../../shared/dynamic-form/form-schema.models';
import { DynamicApiService, DynamicEntity } from '../dynamic-api.service';
import { FormBuilderCanvasComponent } from './form-builder-canvas.component';
import { FormBuilderLayoutPickerComponent } from './form-builder-layout-picker.component';
import { FormBuilderPaletteComponent } from './form-builder-palette.component';
import { FormBuilderPropertyPanelComponent } from './form-builder-property-panel.component';
import { FormBuilderSectionManagerComponent } from './form-builder-section-manager.component';
import { AiAuthoringWizardComponent } from '../ai-authoring/ai-authoring-wizard.component';
import { AiFormDraftAcceptedEvent } from '../ai-authoring/ai-authoring.models';
import { hasAiGeneratePermission } from '../ai-authoring/ai-authoring.util';

@Component({
  standalone: true,
  selector: 'ain-dynamic-form-builder',
  imports: [
    FormsModule,
    ButtonModule,
    ConfirmDialogModule,
    DialogModule,
    SelectModule,
    TabsModule,
    TagModule,
    InfoHintComponent,
    AinStateComponent,
    FormBuilderLayoutPickerComponent,
    FormBuilderPaletteComponent,
    FormBuilderCanvasComponent,
    FormBuilderPropertyPanelComponent,
    FormBuilderSectionManagerComponent,
    FormBuilderLogicPanelComponent,
    FormBuilderActionsPanelComponent,
    FormBuilderPublishPanelComponent,
    FormBuilderAiOutcomePanelComponent,
    FormBuilderThemePanelComponent,
    FormBuilderPreviewComponent,
    FormBuilderLocalizedInputComponent,
    AiAuthoringWizardComponent
  ],
  providers: [MessageService, ConfirmationService, FormBuilderEditCultureService],
  template: `
    <section class="builder-root">
      <div class="builder-header">
        <div class="title-block">
          <h2>{{ t('dynamic.builder.title') }}</h2>
          <ain-info-hint
            [titleKey]="'dynamic.builder.infoTitle'"
            [descriptionKey]="'dynamic.builder.infoDescription'"
            [bulletKeys]="[
              'dynamic.builder.infoBulletLayout',
              'dynamic.builder.infoBulletSave',
              'dynamic.builder.infoBulletDraft'
            ]" />
        </div>
        <div class="header-actions">
          @if (draftStatus()) {
            <p-tag severity="info" [value]="draftStatus()!" />
          }
          @if (canGenerateAi()) {
            <button
              pButton
              type="button"
              severity="help"
              icon="pi pi-sparkles"
              [label]="t('dynamic.aiAuthoring.generateWithAi')"
              (click)="openAiAuthoringWizard()"></button>
          }
          @if (canUpdate()) {
            <div class="lang-toggle" role="group" [attr.aria-label]="t('dynamic.builder.language.toggleLabel')">
              <button
                pButton
                type="button"
                [text]="editCulture() !== 'en-US'"
                [severity]="editCulture() === 'en-US' ? 'primary' : 'secondary'"
                label="{{ t('dynamic.builder.language.en') }}"
                (click)="setEditCulture('en-US')"></button>
              <button
                pButton
                type="button"
                [text]="editCulture() !== 'fa-IR'"
                [severity]="editCulture() === 'fa-IR' ? 'primary' : 'secondary'"
                label="{{ t('dynamic.builder.language.fa') }}"
                (click)="setEditCulture('fa-IR')"></button>
            </div>
            <p-select
              [options]="layoutOptions()"
              optionLabel="label"
              optionValue="value"
              [ngModel]="schema()!.layout.type"
              appendTo="body"
              [placeholder]="t('dynamic.builder.changeLayout')"
              (ngModelChange)="requestLayoutChange($event)" />
            <button pButton type="button" severity="secondary" [label]="t('dynamic.builder.saveVersion')" [disabled]="saving()" (click)="saveVersion()"></button>
          }
          @if (canPublish()) {
            <button pButton type="button" [label]="t('dynamic.publishForm')" [disabled]="publishing()" (click)="publishForm()"></button>
          }
        </div>
      </div>

      @if (loading()) {
        <ain-state type="loading" [title]="t('dynamic.builder.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="t('dynamic.builder.loadError')" [message]="error() ?? ''" />
      } @else if (!schema()) {
        <ain-state type="empty" [title]="t('dynamic.builder.empty')" />
      } @else if (showLayoutPicker()) {
        <ain-form-builder-layout-picker (layoutSelected)="initializeLayout($event)" />
      } @else {
        <p-tabs [value]="builderMode()" (valueChange)="setBuilderMode($event)">
          <p-tablist>
            <p-tab value="design">{{ t('dynamic.builder.mode.design') }}</p-tab>
            <p-tab value="logic">{{ t('dynamic.builder.mode.logic') }}</p-tab>
            <p-tab value="actions">{{ t('dynamic.builder.mode.actions') }}</p-tab>
            <p-tab value="theme">{{ t('dynamic.builder.mode.theme') }}</p-tab>
            <p-tab value="preview">{{ t('dynamic.builder.mode.preview') }}</p-tab>
            <p-tab value="publish">{{ t('dynamic.builder.mode.publish') }}</p-tab>
            <p-tab value="ai">{{ t('dynamic.builder.mode.ai') }}</p-tab>
          </p-tablist>
          <p-tabpanels>
            <p-tabpanel value="design">
              @if (builderMode() === 'design') {
                <div class="builder-grid">
                  <ain-form-builder-palette
                    [disabled]="!canUpdate()"
                    (addField)="addFieldFromPalette($event)" />

                  <div class="center-column">
                    <ain-form-builder-localized-input
                      [label]="t('dynamic.builder.formTitle')"
                      [text]="schema()!.title"
                      [disabled]="!canUpdate()"
                      (textChange)="updateFormTitle($event)" />

                    <ain-form-builder-section-manager
                      [schema]="schema()!"
                      [culture]="culture()"
                      [layoutType]="schema()!.layout.type"
                      [activeSectionKey]="activeSectionKey()"
                      [canEdit]="canUpdate()"
                      (schemaChange)="updateSchema($event)"
                      (activeSectionChange)="setActiveSection($event)"
                      (deleteSection)="confirmDeleteSection($event)" />

                    <ain-form-builder-canvas
                      #canvas
                      [schema]="schema()!"
                      [culture]="culture()"
                      [layoutType]="schema()!.layout.type"
                      [activeSectionKey]="activeSectionKey()"
                      [selectedFieldKey]="selectedFieldKey()"
                      [canEdit]="canUpdate()"
                      (schemaChange)="updateSchema($event)"
                      (activeSectionChange)="setActiveSection($event)"
                      (selectedFieldChange)="setSelectedField($event)"
                      (removeField)="confirmRemoveSelectedField()" />
                  </div>

                  <ain-form-builder-property-panel
                    [field]="selectedField()"
                    [schema]="schema()!"
                    [culture]="culture()"
                    [allFieldKeys]="fieldKeys()"
                    [canEdit]="canUpdate()"
                    [showSpan]="isGridLayout()"
                    [entities]="entities()"
                    [entityFieldMap]="entityFieldMap()"
                    (fieldChange)="updateSelectedField($event)"
                    (fieldRemove)="confirmRemoveSelectedField()" />
                </div>
              }
            </p-tabpanel>
            <p-tabpanel value="logic">
              @if (builderMode() === 'logic') {
                <ain-form-builder-logic-panel />
              }
            </p-tabpanel>
            <p-tabpanel value="actions">
              @if (builderMode() === 'actions') {
                <ain-form-builder-actions-panel
                  [schema]="schema()!"
                  [culture]="culture()"
                  [entities]="entities()"
                  [entityFieldMap]="entityFieldMap()"
                  [canEdit]="canUpdate()"
                  (eventsChange)="patchSchemaSection({ events: $event })"
                  (scriptsChange)="patchSchemaSection({ scripts: $event })"
                  (webhooksChange)="patchSchemaSection({ webhooks: $event })"
                  (submitActionsChange)="patchSchemaSection({ submitActions: $event })" />
              }
            </p-tabpanel>
            <p-tabpanel value="theme">
              @if (builderMode() === 'theme') {
                <ain-form-builder-theme-panel
                  [theme]="schema()!.theme ?? { preset: 'default', rtl: 'auto' }"
                  [canEdit]="canUpdate()"
                  (themeChange)="patchSchemaSection({ theme: $event })" />
              }
            </p-tabpanel>
            <p-tabpanel value="preview">
              @if (builderMode() === 'preview') {
                <ain-form-builder-preview [schema]="schema()!" />
              }
            </p-tabpanel>
            <p-tabpanel value="publish">
              @if (builderMode() === 'publish') {
                <ain-form-builder-publish-panel
                  [publicAccess]="schema()!.publicAccess ?? {}"
                  [canEdit]="canUpdate()"
                  [canPublish]="canPublish()"
                  (publicAccessChange)="patchSchemaSection({ publicAccess: $event })"
                  (slugSuggestion)="publicationSlugSuggestion.emit($event)" />
              }
            </p-tabpanel>
            <p-tabpanel value="ai">
              @if (builderMode() === 'ai') {
                <ain-form-builder-ai-outcome-panel
                  [aiOutcome]="schema()!.aiOutcome ?? defaultAiOutcome()"
                  [culture]="culture()"
                  [canEdit]="canUpdate()"
                  (aiOutcomeChange)="patchSchemaSection({ aiOutcome: $event })" />
              }
            </p-tabpanel>
          </p-tabpanels>
        </p-tabs>
      }
    </section>

    <p-dialog
      [header]="t('dynamic.builder.draftRestore.title')"
      [modal]="true"
      [visible]="draftDialogVisible()"
      (visibleChange)="draftDialogVisible.set($event)">
      <p>{{ t('dynamic.builder.draftRestore.message') }}</p>
      <p>{{ t('dynamic.builder.draftRestore.updatedAt') }}: {{ draftUpdatedAt() }}</p>
      <ng-template pTemplate="footer">
        <button pButton type="button" severity="secondary" [label]="t('dynamic.builder.draftRestore.discard')" (click)="discardDraft()"></button>
        <button pButton type="button" [label]="t('dynamic.builder.draftRestore.restore')" (click)="restoreDraft()"></button>
      </ng-template>
    </p-dialog>

    <p-confirmDialog />

    <ain-ai-authoring-wizard
      #aiWizard
      mode="form"
      [(visible)]="aiWizardVisible"
      (formDraftAccepted)="loadAiFormDraft($event)" />
  `,
  styles: [`
    .builder-root { display: grid; gap: 16px; }
    .builder-header {
      display: flex;
      justify-content: space-between;
      gap: 12px;
      flex-wrap: wrap;
      align-items: flex-start;
    }
    .title-block { display: grid; gap: 8px; }
    .title-block h2 { margin: 0; }
    .header-actions { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .lang-toggle { display: inline-flex; gap: 4px; }
    .builder-grid {
      display: grid;
      grid-template-columns: minmax(220px, 260px) minmax(0, 1fr) minmax(280px, 320px);
      gap: 12px;
      align-items: start;
    }
    .center-column { display: grid; gap: 12px; }
    @media (max-width: 1200px) {
      .builder-grid { grid-template-columns: 1fr; }
    }
  `]
})
export class DynamicFormBuilderComponent implements OnInit {
  private readonly api = inject(DynamicApiService);
  private readonly localization = inject(LocalizationService);
  private readonly contextService = inject(CurrentContextService);
  private readonly messages = inject(MessageService);
  private readonly confirmation = inject(ConfirmationService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly editCultureService = inject(FormBuilderEditCultureService);

  readonly formId = input.required<string>();

  readonly saved = output<void>();
  readonly published = output<void>();
  readonly publicationSlugSuggestion = output<string>();

  readonly canvas = viewChild(FormBuilderCanvasComponent);
  readonly aiWizard = viewChild<AiAuthoringWizardComponent>('aiWizard');

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly publishing = signal(false);
  readonly error = signal<string | null>(null);
  readonly schema = signal<FormSchemaModel | null>(null);
  readonly activeSectionKey = signal<string | null>(null);
  readonly selectedFieldKey = signal<string | null>(null);
  readonly draftDialogVisible = signal(false);
  readonly draftPayload = signal<FormBuilderDraftPayload | null>(null);
  readonly draftUpdatedAt = signal('');
  readonly draftStatus = signal<string | null>(null);
  readonly entities = signal<DynamicEntity[]>([]);
  readonly entityFieldMap = signal<Record<string, { label: string; value: string }[]>>({});
  readonly builderMode = signal<'design' | 'logic' | 'actions' | 'theme' | 'preview' | 'publish' | 'ai'>('design');
  readonly editCulture = this.editCultureService.editCulture;
  private readonly layoutReady = signal(false);
  aiWizardVisible = false;

  private autosaveTimer: ReturnType<typeof setTimeout> | null = null;
  private runtimeSchemaJson = '';

  readonly culture = computed(() => this.localization.activeCulture());
  readonly showLayoutPicker = computed(() => {
    const schema = this.schema();
    if (!schema || this.layoutReady()) {
      return false;
    }
    return schema.sections.every(section => section.fields.length === 0)
      && !readLocalizedTextPresent(schema.title);
  });
  readonly fieldKeys = computed(() => this.schema() ? collectFieldKeys(this.schema()!) : []);
  readonly selectedField = computed(() => {
    const schema = this.schema();
    const key = this.selectedFieldKey();
    if (!schema || !key) {
      return null;
    }
    return findField(schema, key)?.field ?? null;
  });
  readonly layoutOptions = computed(() =>
    FORM_LAYOUT_TYPES.map(item => ({ label: this.t(item.labelKey), value: item.value }))
  );

  ngOnInit(): void {
    this.loadBuilder();
    this.loadEntities();
  }

  isGridLayout(): boolean {
    const type = this.schema()?.layout.type;
    return !!type && ['twoColumn', 'threeColumn', 'grid'].includes(type);
  }

  initializeLayout(layoutType: FormLayoutType): void {
    const next = createEmptySchema(layoutType, this.culture());
    this.applySchema(next, null);
    this.activeSectionKey.set(next.sections[0]?.key ?? null);
    this.layoutReady.set(true);
    this.builderMode.set('design');
    this.queueAutosave();
  }

  updateSchema(next: FormSchemaModel): void {
    this.schema.set(next);
    this.queueAutosave();
  }

  setBuilderMode(mode: string | number | undefined): void {
    if (
      mode === 'design'
      || mode === 'logic'
      || mode === 'actions'
      || mode === 'theme'
      || mode === 'preview'
      || mode === 'publish'
      || mode === 'ai'
    ) {
      this.builderMode.set(mode);
    }
  }

  setEditCulture(culture: 'en-US' | 'fa-IR'): void {
    this.editCultureService.setEditCulture(culture);
  }

  updateFormTitle(title: FormSchemaModel['title']): void {
    const current = this.schema();
    if (!current) {
      return;
    }
    this.updateSchema({ ...current, title });
  }

  patchSchemaSection(section: {
    events?: FormEvents;
    scripts?: FormScript[];
    webhooks?: FormWebhook[];
    submitActions?: FormSubmitAction[];
    publicAccess?: FormPublicAccess;
    aiOutcome?: FormAiOutcome;
    theme?: FormSchemaModel['theme'];
  }): void {
    const current = this.schema();
    if (!current) {
      return;
    }
    this.updateSchema({ ...current, ...section });
  }

  setActiveSection(sectionKey: string): void {
    this.activeSectionKey.set(sectionKey);
    this.queueAutosave();
  }

  setSelectedField(fieldKey: string | null): void {
    this.selectedFieldKey.set(fieldKey);
  }

  updateSelectedField(field: FormField): void {
    const schema = this.schema();
    const key = this.selectedFieldKey();
    if (!schema || !key) {
      return;
    }

    const sections = schema.sections.map(section => ({
      ...section,
      fields: section.fields.map(item => item.key === key ? field : item)
    }));
    this.updateSchema({ ...schema, sections });
  }

  addFieldFromPalette(type: FormFieldType): void {
    this.canvas()?.addFieldToActiveSection(type);
  }

  requestLayoutChange(nextLayout: FormLayoutType): void {
    const current = this.schema();
    if (!current || current.layout.type === nextLayout) {
      return;
    }

    this.confirmation.confirm({
      header: this.t('dynamic.builder.layoutChange.title'),
      message: this.t('dynamic.builder.layoutChange.message'),
      accept: () => {
        const migrated = migrateLayoutSchema(current, nextLayout);
        this.applySchema(migrated, this.selectedFieldKey());
        this.messages.add({
          severity: 'success',
          summary: this.t('dynamic.builder.layoutChange.success')
        });
      }
    });
  }

  confirmDeleteSection(sectionKey: string): void {
    const schema = this.schema();
    if (!schema || schema.sections.length <= 1) {
      return;
    }

    this.confirmation.confirm({
      header: this.t('dynamic.builder.section.deleteTitle'),
      message: this.t('dynamic.builder.section.deleteMessage'),
      accept: () => {
        const sections = schema.sections.filter(section => section.key !== sectionKey);
        const nextKey = sections[0]?.key ?? null;
        this.applySchema({ ...schema, sections }, null);
        this.activeSectionKey.set(nextKey);
      }
    });
  }

  confirmRemoveSelectedField(): void {
    const schema = this.schema();
    const key = this.selectedFieldKey();
    if (!schema || !key || !this.canUpdate()) {
      return;
    }

    this.confirmation.confirm({
      header: this.t('dynamic.builder.properties.removeFieldConfirmTitle'),
      message: this.t('dynamic.builder.properties.removeFieldConfirmMessage'),
      accept: () => {
        const sections = schema.sections.map(section => ({
          ...section,
          fields: section.fields.filter(field => field.key !== key)
        }));
        this.updateSchema({ ...schema, sections });
        this.selectedFieldKey.set(null);
      }
    });
  }

  saveVersion(): void {
    const schema = this.schema();
    const formId = this.formId();
    if (!schema || !formId || !this.canUpdate()) {
      return;
    }

    this.saving.set(true);
    const schemaJson = serializeFormSchema(schema);
    this.api.addFormVersion(formId, schemaJson)
      .pipe(finalize(() => this.saving.set(false)))
      .subscribe({
        next: () => {
          this.runtimeSchemaJson = schemaJson;
          this.api.deleteFormDraft(formId).subscribe({ next: () => undefined, error: () => undefined });
          this.draftStatus.set(null);
          this.messages.add({ severity: 'success', summary: this.t('dynamic.builder.saveSuccess') });
          this.saved.emit();
        },
        error: () => this.messages.add({ severity: 'error', summary: this.t('common.saveFailed') })
      });
  }

  publishForm(): void {
    const formId = this.formId();
    if (!formId || !this.canPublish()) {
      return;
    }

    this.publishing.set(true);
    this.api.publishForm(formId)
      .pipe(finalize(() => this.publishing.set(false)))
      .subscribe({
        next: () => {
          this.messages.add({ severity: 'success', summary: this.t('dynamic.published') });
          this.published.emit();
        },
        error: () => this.messages.add({ severity: 'error', summary: this.t('common.saveFailed') })
      });
  }

  restoreDraft(): void {
    const draft = this.draftPayload();
    if (!draft) {
      this.draftDialogVisible.set(false);
      return;
    }
    this.applySchema(draft.schema, draft.selection.fieldKey);
    this.activeSectionKey.set(draft.activeSectionKey ?? draft.schema.sections[0]?.key ?? null);
    this.layoutReady.set(true);
    this.draftDialogVisible.set(false);
  }

  discardDraft(): void {
    const formId = this.formId();
    if (formId) {
      this.api.deleteFormDraft(formId).subscribe({ next: () => undefined, error: () => undefined });
    }
    this.draftDialogVisible.set(false);
    this.draftPayload.set(null);
  }

  canUpdate(): boolean {
    return this.hasFormAction('Update') || this.hasFormAction('Create');
  }

  canPublish(): boolean {
    return this.hasFormAction('Publish');
  }

  canGenerateAi(): boolean {
    return hasAiGeneratePermission(this.contextService.context()) && this.canUpdate();
  }

  openAiAuthoringWizard(): void {
    this.aiWizard()?.open();
  }

  loadAiFormDraft(event: AiFormDraftAcceptedEvent): void {
    this.applySchema(event.schema, event.schema.sections[0]?.fields[0]?.key ?? null);
    this.activeSectionKey.set(event.schema.sections[0]?.key ?? null);
    this.layoutReady.set(true);
    this.builderMode.set('design');
    this.queueAutosave();

    const draftId = event.draftId;
    const formId = this.formId();
    if (draftId && formId) {
      this.api.markAiDraftApplied(draftId, { formId }).subscribe({ error: () => undefined });
    }
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  defaultAiOutcome(): FormAiOutcome {
    return createDefaultAiOutcome(this.editCulture());
  }

  private loadBuilder(): void {
    const formId = this.formId();
    this.loading.set(true);
    this.error.set(null);

    forkJoin({
      runtime: this.api.getFormRuntime(formId),
      draft: this.api.getFormDraft(formId).pipe(switchMap(draft => of(draft)))
    })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.loading.set(false))
      )
      .subscribe({
        next: ({ runtime, draft }) => {
          this.runtimeSchemaJson = runtime.schemaSnapshot;
          const parsed = parseFormSchema(runtime.schemaSnapshot, this.culture());
          if (!parsed) {
            this.error.set(this.t('dynamic.builder.parseError'));
            return;
          }

          const runtimeSchema = applyActivePublicationSlug(parsed, runtime.activePublicationSlug);

          if (draft?.draftDataJson) {
            const draftPayload = parseBuilderDraft(draft.draftDataJson, this.culture());
            if (draftPayload && this.isDraftNewer(draftPayload, runtimeSchema, draft.updatedAtUtc)) {
              this.draftPayload.set({
                ...draftPayload,
                schema: applyActivePublicationSlug(draftPayload.schema, runtime.activePublicationSlug)
              });
              this.draftUpdatedAt.set(draft.updatedAtUtc ?? '');
              this.draftDialogVisible.set(true);
            }
          }

          this.applySchema(runtimeSchema, null);
          this.syncLayoutReadyFromSchema(runtimeSchema);
        },
        error: () => this.error.set(this.t('dynamic.builder.loadError'))
      });
  }

  private loadEntities(): void {
    this.api.listEntities()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: entities => {
          this.entities.set(entities);
          for (const entity of entities) {
            this.api.getEntity(entity.id)
              .pipe(takeUntilDestroyed(this.destroyRef))
              .subscribe({
                next: detail => {
                  this.entityFieldMap.update(current => ({
                    ...current,
                    [entity.id]: detail.fields.map(field => ({
                      label: `${field.displayName} (${field.name})`,
                      value: field.name
                    }))
                  }));
                }
              });
          }
        }
      });
  }

  private isDraftNewer(draft: FormBuilderDraftPayload, runtimeSchema: FormSchemaModel, updatedAtUtc?: string): boolean {
    if (updatedAtUtc) {
      return true;
    }
    return serializeFormSchema(draft.schema) !== serializeFormSchema(runtimeSchema);
  }

  private applySchema(schema: FormSchemaModel, selectedFieldKey: string | null): void {
    this.schema.set(schema);
    this.activeSectionKey.set(this.activeSectionKey() ?? schema.sections[0]?.key ?? null);
    this.selectedFieldKey.set(selectedFieldKey);
  }

  private syncLayoutReadyFromSchema(schema: FormSchemaModel): void {
    const hasContent = readLocalizedTextPresent(schema.title)
      || schema.sections.some(section => section.fields.length > 0);
    this.layoutReady.set(hasContent);
  }

  private queueAutosave(): void {
    if (!this.canUpdate()) {
      return;
    }

    if (this.autosaveTimer) {
      clearTimeout(this.autosaveTimer);
    }

    this.autosaveTimer = setTimeout(() => this.persistDraft(), 1500);
    this.draftStatus.set(this.t('dynamic.builder.draftPending'));
  }

  private persistDraft(): void {
    const schema = this.schema();
    const formId = this.formId();
    if (!schema || !formId) {
      return;
    }

    const payload: FormBuilderDraftPayload = {
      draftVersion: 1,
      schema,
      selection: {
        sectionKey: this.activeSectionKey(),
        fieldKey: this.selectedFieldKey()
      },
      activeSectionKey: this.activeSectionKey()
    };

    this.api.saveFormDraft(formId, serializeBuilderDraft(payload))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: draft => {
          this.draftStatus.set(this.t('dynamic.builder.draftSaved'));
          this.draftUpdatedAt.set(draft.updatedAtUtc ?? '');
        },
        error: () => this.draftStatus.set(this.t('dynamic.builder.draftError'))
      });
  }

  private hasFormAction(action: string): boolean {
    const context = this.contextService.context();
    const roles = new Set(context?.roles ?? []);
    if (roles.has('SystemAdmin')) {
      return true;
    }

    const node = (context?.permissions ?? []).find(item => item.key === 'dynamic.forms');
    if (!node) {
      return false;
    }

    const actions = new Set((node.actions ?? []).map(item => item.toLowerCase()));
    return actions.has(action.toLowerCase());
  }
}

function readLocalizedTextPresent(title: FormSchemaModel['title']): boolean {
  return Object.values(title ?? {}).some(value => value.trim().length > 0);
}
